// SECURE Cloudflare R2 Storage Service - Uses server-side API
// NO SECRET KEYS IN CLIENT CODE!

import { supabase } from '../lib/supabase';

class CloudflareR2Service {
  constructor() {
    // Use relative URLs in production (same domain as Vercel functions),
    // fallback to localhost in dev if REACT_APP_API_URL is set.
    this.apiUrl = process.env.REACT_APP_API_URL || '';
    this.publicUrl = process.env.REACT_APP_CLOUDFLARE_R2_PUBLIC_URL;
  }

  // Get auth token from Supabase
  async getAuthToken() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }
    return session.access_token;
  }

  // Upload logo using server-side presigned URL
  async uploadLogo(file) {
    try {
      // Validate file
      this.validateFile(file);
      
      console.log('📤 Starting secure logo upload...');
      
      // Step 1: Get presigned URL from backend
      const authToken = await this.getAuthToken();
      
      const response = await fetch(`${this.apiUrl}/api/upload/generate-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || error.error || 'Failed to get upload URL');
      }
      
      const { uploadUrl, publicUrl } = await response.json();
      
      // Step 2: Upload directly to R2 using presigned URL
      console.log('🚀 Uploading to R2...');
      
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        }
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Upload to storage failed');
      }
      
      console.log('✅ Upload successful');
      
      // Track analytics
      if (window.analytics) {
        window.analytics.track('Logo Uploaded', {
          fileSize: file.size,
          fileType: file.type,
          storage: 'cloudflare-r2'
        });
      }
      
      return { url: publicUrl };
      
    } catch (error) {
      console.error('❌ Logo upload failed:', error);
      
      // User-friendly error messages
      if (error.message.includes('authentication')) {
        throw new Error('Please log in to upload files');
      } else if (error.message.includes('Failed to fetch')) {
        throw new Error(`Cannot reach the API server at ${this.apiUrl}. Start the backend server and try again.`);
      } else if (error.message.includes('Network')) {
        throw new Error('Network error. Please check your connection and try again.');
      } else {
        throw new Error(error.message || 'Failed to upload logo');
      }
    }
  }

  // Delete file using server-side API
  async deleteFile(fileUrl) {
    try {
      const authToken = await this.getAuthToken();
      
      const response = await fetch(`${this.apiUrl}/api/upload/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ fileUrl })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || error.error || 'Failed to delete file');
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting file:', error);
      throw new Error('Failed to delete file');
    }
  }

  // File validation (client-side check, also validated on server)
  validateFile(file) {
    const maxSize = 2 * 1024 * 1024; // 2MB
    // SVG removed for security - can contain malicious scripts
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    
    if (file.size > maxSize) {
      throw new Error('File size must be less than 2MB');
    }
    
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Only JPEG, PNG, and WebP files are allowed');
    }
  }

  // Get optimized image URL (if Cloudflare Image Resizing is enabled)
  getOptimizedUrl(url, options = {}) {
    if (!url) return '';
    
    // If image resizing is enabled on your Cloudflare zone
    if (process.env.REACT_APP_CLOUDFLARE_IMAGE_RESIZING_ENABLED === 'true') {
      const { width, height, quality = 85 } = options;
      const params = [];
      
      if (width) params.push(`width=${width}`);
      if (height) params.push(`height=${height}`);
      params.push(`quality=${quality}`);
      params.push('format=auto');
      
      return `${url}?${params.join('&')}`;
    }
    
    return url;
  }

  // Check if service is properly configured
  isConfigured() {
    return !!(this.apiUrl && this.publicUrl);
  }
}

// ALTERNATIVE: Use Supabase Storage (simpler, no R2 needed)
export class SupabaseStorageService {
  async uploadLogo(file) {
    try {
      // Validate file
      this.validateFile(file);

      const timestamp = Date.now();
      const fileName = `${timestamp}-${file.name}`;

      // Upload to Supabase storage
      const { data, error } = await supabase.storage
        .from('logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      return { url: publicUrl };
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error(error.message || 'Failed to upload logo');
    }
  }

  async deleteFile(fileUrl) {
    try {
      if (!fileUrl) return { success: true };

      // Extract filename from Supabase public URL
      // URL format: https://<project>.supabase.co/storage/v1/object/public/logos/<filename>
      const url = new URL(fileUrl);
      const pathParts = url.pathname.split('/');
      // pathname: /storage/v1/object/public/logos/<filename>
      const logosIndex = pathParts.indexOf('logos');
      if (logosIndex === -1 || logosIndex >= pathParts.length - 1) {
        console.warn('Could not extract filename from URL:', fileUrl);
        return { success: false };
      }
      const fileName = pathParts.slice(logosIndex + 1).join('/');

      const { error } = await supabase.storage
        .from('logos')
        .remove([fileName]);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Delete error:', error);
      // Don't throw — deleting from storage is best-effort
      return { success: false };
    }
  }

  validateFile(file) {
    const maxSize = 2 * 1024 * 1024; // 2MB
    // SVG removed for security - can contain malicious scripts
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    if (file.size > maxSize) {
      throw new Error('File size must be less than 2MB');
    }

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Only JPEG, PNG, and WebP files are allowed');
    }
  }
}

// Export the secure R2 service (requires backend endpoints)
export const r2Storage = new CloudflareR2Service();

// Export Supabase storage as an alternative (works immediately)
export const supabaseStorage = new SupabaseStorageService();

export default r2Storage;
